import type { CollectionDef } from "@/store/EntityStore";
import type * as t from "@/store/types";

/**
 * Every entity collection in the store, keyed the way the old PGlite tables were keyed. Names are
 * the wire/bootstrap entity names; one collection per former table.
 */
export type StoreSchema = {
  account: t.Account;
  accountEvent: t.AccountEvent;
  appPhoneNumber: t.AppPhoneNumber;
  audioEncoding: t.AudioEncoding;
  audioQueueItem: t.AudioQueueItem;
  broadcast: t.Broadcast;
  broadcastAction: t.BroadcastAction;
  broadcastRecipient: t.BroadcastRecipient;
  callConfiguration: t.CallConfiguration;
  callRecord: t.CallRecord;
  currentRoute: t.CurrentRouteRow;
  directWsInvitation: t.DirectWsInvitation;
  displayArtifact: t.DisplayArtifact;
  feed: t.Feed;
  feedAcknowledgement: t.FeedAcknowledgementRow;
  feedItemAcknowledgement: t.FeedItemAcknowledgementRow;
  feedGroup: t.FeedGroup;
  feedGroupMembership: t.FeedGroupMembership;
  feedPreference: t.FeedPreference;
  file: t.File;
  fleetGroup: t.FleetGroup;
  fleetGroupItem: t.FleetGroupItem;
  item: t.Item;
  link: t.Link;
  permission: t.Permission;
  scheduleTrigger: t.ScheduleTrigger;
  anchorAcknowledgement: t.AnchorAcknowledgementRow;
  taskAcknowledgement: t.TaskAcknowledgementRow;
  task: t.TaskRow;
  taskPinBoard: t.TaskPinBoardRow;
  tag: t.Tag;
  template: t.Template;
  tmsEvent: t.TmsEventRow;
  transcription: t.Transcription;
  workflowItem: t.WorkflowItem;
  workspace: t.Workspace;
  workspaceCommandAlias: t.WorkspaceCommandAlias;
  workspaceMembership: t.WorkspaceMembership;
  workspaceMembershipTag: t.WorkspaceMembershipTag;
  wsMembershipAvailability: t.MembershipAvailability;
};

export type CollectionName = keyof StoreSchema;

export type RowOf<K extends CollectionName> = StoreSchema[K];

const byId: CollectionDef<{ id: string }> = { key: (row) => row.id };

export function membershipTagKey(row: Pick<t.WorkspaceMembershipTag, "workspaceMembershipId" | "tagId" | "tagValue">) {
  return `${row.workspaceMembershipId}\u0000${row.tagId}\u0000${row.tagValue}`;
}

/**
 * The old table's uniqueness was the composite `(feedId, contentId, itemId, name, accountId)` with
 * nulls not distinct — the same logical event upserts in place even when the server issues a new
 * id. Keying the collection the same way preserves that for the receipts, which are idempotent.
 */
export function accountEventKey(
  row: Pick<t.AccountEvent, "feedId" | "contentId" | "itemId" | "name" | "accountId">,
): string {
  return `${row.feedId}\u0000${row.contentId}\u0000${row.itemId}\u0000${row.name}\u0000${row.accountId}`;
}

export const collectionDefs: { [K in CollectionName]: CollectionDef<StoreSchema[K]> } = {
  account: byId,
  accountEvent: { key: accountEventKey },
  appPhoneNumber: byId,
  audioEncoding: byId,
  audioQueueItem: byId,
  broadcast: byId,
  broadcastAction: byId,
  broadcastRecipient: byId,
  callConfiguration: byId,
  callRecord: byId,
  // One row per driver; the workspace read answers each driver once, so the membership is the key.
  currentRoute: { key: (row) => row.workspaceMembershipId },
  directWsInvitation: byId,
  displayArtifact: byId,
  feed: byId,
  // The acknowledgement log's rows are facts keyed by their client-minted id; the fold needs every one of them.
  feedAcknowledgement: byId,
  feedItemAcknowledgement: byId,
  feedGroup: byId,
  // Uniqueness is one group membership per feed per workspace (the old unique index); the id is an
  // attribute the server may reissue.
  feedGroupMembership: { key: (row) => `${row.workspaceId}\u0000${row.feedId}` },
  feedPreference: byId,
  file: byId,
  fleetGroup: byId,
  fleetGroupItem: byId,
  item: byId,
  link: byId,
  permission: byId,
  scheduleTrigger: byId,
  anchorAcknowledgement: byId,
  // The acknowledgement log's rows are facts keyed by their client-minted id; the fold needs every one of them.
  taskAcknowledgement: byId,
  // A task and a pin are facts keyed by their client-minted id, so an optimistic row and the server's echo are one row.
  task: byId,
  taskPinBoard: byId,
  tag: byId,
  template: byId,
  tmsEvent: byId,
  transcription: byId,
  workflowItem: byId,
  workspace: byId,
  workspaceCommandAlias: byId,
  workspaceMembership: byId,
  workspaceMembershipTag: { key: membershipTagKey },
  wsMembershipAvailability: { key: (row) => row.workspaceMembershipId },
};

/**
 * Collections wiped by a full purge. Mirrors the old schema `tables` export, which deliberately
 * spared local-only fleet groups (and kept the audio queue, app phone numbers, etc. in the wipe).
 */
export const purgeableCollections: readonly CollectionName[] = (Object.keys(collectionDefs) as CollectionName[]).filter(
  (name) => name !== "fleetGroup" && name !== "fleetGroupItem",
);
